import type { ShipmentDetail } from "api";
import type { useShipmentDocuments } from "hooks/useShipmentDocuments";
import { UploadIcon } from "./docs.icons";
import { containerLabel, documentsSub } from "./docs.model";
import { DocumentItem } from "./DocumentItem";
import { ShipmentDrawer } from "./ShipmentDrawer";
import { useDocumentActions } from "./useDocumentActions";

type DocumentsDrawerProps = {
  shipment: ShipmentDetail;
  docs: ReturnType<typeof useShipmentDocuments>;
  /** One container's documents, or null for every document on the shipment. */
  containerId: number | null;
  onClose: () => void;
};

/**
 * `#shdoc` holding documents: one container's, or the whole shipment's with
 * each container named beside its files. Uploads land in the same scope.
 */
export const DocumentsDrawer = ({ shipment, docs, containerId, onClose }: DocumentsDrawerProps) => {
  const actions = useDocumentActions(docs);
  const index = shipment.containers.findIndex((container) => container.id === containerId);
  const container = index === -1 ? null : shipment.containers[index];
  const documents =
    containerId === null ? docs.documents : docs.documents.filter((document) => document.container_id === containerId);

  return (
    <ShipmentDrawer
      kind={containerId === null ? "shipment" : "container"}
      title={containerId === null ? "Shipment Documents" : "Container Documents"}
      sub={container === null ? documentsSub(shipment) : containerLabel(shipment, container, index)}
      action={
        <button
          type="button"
          className="pcbtn shdoc__up"
          disabled={docs.status !== "ready" || actions.uploading}
          onClick={() => actions.chooseFile(containerId)}
        >
          <UploadIcon />
          {actions.uploading ? "Uploading…" : "Upload"}
        </button>
      }
      onClose={onClose}
    >
      {actions.error !== null && <p className="pc-note pc-note--bad shdoc__err">{actions.error}</p>}

      {docs.status === "loading" && <span className="pc-spin shdoc__spin" aria-label="Loading" />}

      {(docs.status === "failed" || docs.status === "unverified") && (
        <div className="shipp__state-msg">
          <p className="pc-note pc-note--bad">Could not load the documents.</p>
          <button type="button" className="pcbtn" onClick={docs.retry}>
            Try again
          </button>
        </div>
      )}

      {docs.status === "ready" && documents.length === 0 && <div className="shdoc__none">No documents yet</div>}

      {docs.status === "ready" &&
        documents.map((document) => (
          <DocumentItem
            key={document.id}
            document={document}
            showContainer={containerId === null}
            onDelete={actions.askDelete}
          />
        ))}

      {actions.elements}
    </ShipmentDrawer>
  );
};
